import type { JsonObject } from "@/types/json"
import type { ScriptTurn } from "@/types/ScriptTurn"
import { generateShortUUID } from "@/utils/id-generation/short-uuid"

import type { EditableTestCase, PendingChanges } from "../model"

/** Parse numbered lines (e.g. "1. Hello\n2. World") into an array of turn strings. */
function parseInputStrToTurns(input: string): string[] | null {
  if (!input.trim()) {
    return null
  }
  const lines = input.split("\n").filter((l) => l.trim())
  // Check if at least the first line looks numbered
  if (!/^\d+\.\s/.test(lines[0])) {
    return null
  }
  return lines.map((line) => line.replace(/^\d+\.\s*/, ""))
}

interface ValidationResult<T> {
  isValid: boolean
  error?: string
  data?: T
}

/**
 * Sentinel a caller returns to explicitly clear a string field (set it to "").
 * The staged-editor validator treats a bare "" as "no change" so an empty
 * value can't be distinguished from "field omitted"; this sentinel is the only
 * way to request a clear through the staged path.
 */
export const CLEAR_SENTINEL = "__CLEAR__"

/**
 * Validate edit updates
 */
export function validateEditUpdates(
  updates: Array<{
    id: string
    input_str?: string | null
    description?: string | null
    expected_behaviors?: string[] | null
    input_type?: string | null
    expected_output_json?: JsonObject | null
    script_turns?: ScriptTurn[] | null
  }>,
): ValidationResult<Array<Partial<EditableTestCase> & { id: string }>> {
  if (!Array.isArray(updates) || updates.length === 0) {
    return {
      isValid: false,
      error: "Updates must be a non-empty array",
    }
  }

  // Returns the resolved value if the field changed, or undefined if it didn't.
  // null/undefined/empty string = no change; "__CLEAR__" = set to empty string.
  const resolveField = (val: string | null | undefined): string | undefined => {
    if (val == null || val === "") {
      return
    }
    return val === CLEAR_SENTINEL ? "" : val
  }

  const validUpdates = updates.map((update) => {
    const input_str = resolveField(update.input_str)
    const description = resolveField(update.description)
    const input_type = resolveField(update.input_type)

    // If editing to SCRIPT type with input_str but no script_turns, parse it
    const isScriptEdit = input_type === "SCRIPT"
    const scriptTurns =
      update.script_turns ??
      (isScriptEdit && input_str ? parseInputStrToTurns(input_str) : null)

    return {
      id: update.id,
      ...(input_str != null && { input_str }),
      ...(description != null && { description }),
      ...(update.expected_behaviors != null && {
        expected_behaviors: update.expected_behaviors,
      }),
      ...(input_type != null && { input_type }),
      ...(update.expected_output_json !== undefined &&
        update.expected_output_json !== null &&
        Object.keys(update.expected_output_json).length > 0 && {
          expected_output_json: update.expected_output_json,
        }),
      ...(scriptTurns != null && {
        script_turns: scriptTurns,
      }),
    }
  })

  return {
    isValid: true,
    data: validUpdates,
  }
}

/**
 * Validate new test cases
 */
export function validateNewTestCases(
  testCases: Array<{
    input_str?: string | null
    description?: string | null
    expected_behaviors?: string[] | null
    input_type?: string | null
    expected_output_json?: JsonObject | null
    script_turns?: ScriptTurn[] | null
  }>,
): ValidationResult<EditableTestCase[]> {
  if (!Array.isArray(testCases) || testCases.length === 0) {
    return {
      isValid: false,
      error: "Test cases must be a non-empty array",
    }
  }

  const validTestCases: EditableTestCase[] = testCases.map((tc) => {
    const isScript = tc.input_type === "SCRIPT"
    // If SCRIPT type but model sent input_str instead of script_turns, parse it
    const scriptTurns =
      tc.script_turns ??
      (isScript && tc.input_str ? parseInputStrToTurns(tc.input_str) : null)

    return {
      id: `new-${generateShortUUID()}`,
      input_str: isScript ? "" : tc.input_str || "",
      description: tc.description ?? undefined,
      expected_behaviors: tc.expected_behaviors || [],
      expected_output_json: tc.expected_output_json ?? null,
      s3_key: null,
      s3_bucket: null,
      input_type: tc.input_type || "SCENARIO",
      script_turns: scriptTurns,
    }
  })

  return {
    isValid: true,
    data: validTestCases,
  }
}

/**
 * Validate delete operations
 */
export function validateDeleteIds(ids: string[]): ValidationResult<string[]> {
  if (!Array.isArray(ids) || ids.length === 0) {
    return {
      isValid: false,
      error: "IDs must be a non-empty array",
    }
  }

  return {
    isValid: true,
    data: ids,
  }
}

/**
 * Check if a test case is pending deletion
 */
export function isPendingDelete(
  testCaseId: string,
  pendingChanges: PendingChanges | null,
): boolean {
  if (pendingChanges?.type !== "DELETE") {
    return false
  }
  return pendingChanges.data.includes(testCaseId)
}

/**
 * Get pending edit for a test case
 */
export function getPendingEditForTestCase(
  testCaseId: string,
  pendingChanges: PendingChanges | null,
): Partial<EditableTestCase> | null {
  if (pendingChanges?.type !== "EDIT") {
    return null
  }
  const edit = pendingChanges.data.find((e) => e.id === testCaseId)
  return edit || null
}
